import { useState, useEffect } from "react";
import { motion } from "framer-motion";

import { View, Text, Button } from "../base";
import { Icon } from "../base/Icon";
import { DeploymentDetailsCard } from "./DeploymentDetailsCard";
import { DeploymentComparison } from "./DeploymentComparison";
import { AgentDeployment } from "../../redux/reducers/agentDeploymentSlice";

interface DeploymentDetailsDialogProps {
  deployment: AgentDeployment | null;
  availableDeployments: AgentDeployment[];
  isOpen: boolean;
  onClose: () => void;
  getFunctionName?: (environmentId: string, deployment?: AgentDeployment) => string;
  getFunctionInfo?: (environmentId: string, deployment?: AgentDeployment) => { name: string; version?: string } | null;
  getEnvironmentName?: (environmentId: string) => string;
}

export const DeploymentDetailsDialog = ({
  deployment,
  availableDeployments,
  isOpen,
  onClose,
  getFunctionName,
  getFunctionInfo,
  getEnvironmentName,
}: DeploymentDetailsDialogProps) => {
  const [compareWith, setCompareWith] = useState<AgentDeployment | null>(null);
  const [showComparison, setShowComparison] = useState(false);

  // Reset comparison state when deployment changes
  useEffect(() => {
    setShowComparison(false);
    setCompareWith(null);
  }, [deployment?.id]);

  const handleCompare = (selectedDeployment: AgentDeployment) => {
    // Sort deployments by deployment date (newest first)
    const sortedDeployments = availableDeployments.sort((a, b) => new Date(b.deployedAt).getTime() - new Date(a.deployedAt).getTime());

    // Find the current deployment index
    const currentIndex = sortedDeployments.findIndex((d) => d.id === selectedDeployment.id);

    // Get the previous deployment (chronologically before current)
    const previousDeployment = sortedDeployments[currentIndex + 1];

    if (previousDeployment) {
      setCompareWith(previousDeployment);
      setShowComparison(true);
    }
  };

  const handleCompareWithSpecific = (targetDeployment: AgentDeployment) => {
    setCompareWith(targetDeployment);
    setShowComparison(true);
  };

  const resetComparison = () => {
    setShowComparison(false);
    setCompareWith(null);
  };

  if (!isOpen || !deployment) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <View className="flex items-center justify-between py-2 px-6 border-b border-gray-200 dark:border-gray-700">
          <Text className="text-xl font-semibold text-gray-900 dark:text-white">{showComparison ? "Deployment Comparison" : "Deployment Details"}</Text>
          <View className="flex items-center gap-2">
            {showComparison && (
              <Button
                variant="ghost"
                onClick={resetComparison}
              >
                <Icon
                  name="arrow-left"
                  size={16}
                  className="mr-1"
                />
                Back to Details
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={onClose}
              className="p-2"
            >
              <Icon
                name="close"
                size={20}
              />
            </Button>
          </View>
        </View>

        {/* Content */}
        <View className="overflow-y-auto max-h-[calc(90vh-80px)]">
          {showComparison && compareWith ? (
            <motion.div
              key="comparison"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="p-6"
            >
              <DeploymentComparison
                leftDeployment={compareWith}
                rightDeployment={deployment}
                getFunctionInfo={getFunctionInfo}
                getEnvironmentName={getEnvironmentName}
              />
            </motion.div>
          ) : (
            <motion.div
              key="details"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="p-6"
            >
              <DeploymentDetailsCard
                deployment={deployment}
                onCompare={handleCompare}
                className="border-0 shadow-none p-0"
                getFunctionName={getFunctionName}
                availableDeployments={availableDeployments}
              />

              {/* Compare With Section */}
              {availableDeployments.filter((d) => d.id !== deployment.id).length > 0 && (
                <View className="pt-6 border-t border-gray-200 dark:border-gray-700">
                  <Text className="text-lg font-medium text-gray-900 dark:text-white mb-4">Compare With Other Deployments</Text>
                  <View className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {availableDeployments
                      .filter((d) => d.id !== deployment.id)
                      .slice(0, 6)
                      .map((otherDeployment) => (
                        <Button
                          key={otherDeployment.id}
                          variant="ghost"
                          onClick={() => handleCompareWithSpecific(otherDeployment)}
                          className="p-4 text-left border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 m-2"
                        >
                          <View className="flex flex-col w-full min-w-0">
                            <View className="flex items-center justify-between min-w-0">
                              <Text className="font-medium text-gray-900 dark:text-white text-sm truncate mr-2 flex-1">
                                {getFunctionName ? getFunctionName(otherDeployment.environmentId, otherDeployment) : otherDeployment.id}
                              </Text>
                              {otherDeployment.isActive && (
                                <View className="px-2 py-1 bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400 rounded text-xs flex-shrink-0">
                                  Active
                                </View>
                              )}
                            </View>
                            <View className="flex items-center gap-2 min-w-0">
                              <Text className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                                {new Date(otherDeployment.deployedAt).toLocaleString()}
                              </Text>
                            </View>
                          </View>
                        </Button>
                      ))}
                  </View>
                </View>
              )}
            </motion.div>
          )}
        </View>
      </motion.div>
    </div>
  );
};
